import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { ScanBarcode, Sparkles, Loader2 } from 'lucide-react';
import { fmtCurrency, fmtNumber } from '../../lib/localeUtils';

/** Loyalty lookup + redemption block inside the checkout dialog. */
export const POSLoyaltyPanel = ({ loyalty, total }) => {
  const {
    enabled, account, settings, redeemPoints, setRedeemPoints, discount,
    lookupBusy, manualCode, setManualCode, onManualScan, onRedeemAll,
  } = loyalty;

  const balance = account?.balance || 0;
  const minRedeem = settings?.min_redeem_points || 0;

  return (
    <>
      {!account && (
        <div className="rounded-lg border bg-slate-50 dark:bg-slate-950/40 p-2.5 flex items-center gap-2" data-testid="loyalty-scan-block">
          <ScanBarcode className="w-4 h-4 text-primary shrink-0" />
          <Input
            value={manualCode || ''}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onManualScan(); } }}
            placeholder="Type or scan loyalty code (LOY-XXXXXXXX)"
            className="h-8 text-sm flex-1 font-mono uppercase"
            data-testid="loyalty-manual-code-input"
          />
          <Button type="button" size="sm" variant="outline" onClick={onManualScan} disabled={!manualCode} data-testid="loyalty-manual-scan-btn">
            Look up
          </Button>
        </div>
      )}

      {enabled && account && (
        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/40 p-3 space-y-3" data-testid="loyalty-widget">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white grid place-items-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-emerald-900">
                  {account.tier} member · {fmtNumber(balance)} pts
                </div>
                <div className="text-[11px] text-emerald-700">
                  {settings?.program_name || 'Rewards'} · Earn ≈ {fmtNumber(Math.round(total * (parseFloat(settings?.earn_rate) || 1)))} pts on this sale
                </div>
              </div>
            </div>
            <Badge variant="outline" className="bg-white text-emerald-800 border-emerald-300" data-testid="loyalty-tier-badge">
              {account.tier}
            </Badge>
          </div>
          {balance >= minRedeem ? (
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-emerald-900">Redeem points</Label>
                <Input
                  type="number"
                  min={0}
                  max={balance}
                  value={redeemPoints || ''}
                  onChange={(e) => setRedeemPoints(Math.max(0, Math.min(parseInt(e.target.value || 0, 10), balance)))}
                  placeholder="0"
                  className="h-9 text-sm bg-white"
                  data-testid="loyalty-redeem-input"
                />
              </div>
              <Button type="button" variant="outline" size="sm" className="h-9 bg-white" onClick={onRedeemAll} data-testid="loyalty-redeem-all-btn">
                Redeem all
              </Button>
              {discount > 0 && (
                <div className="text-right text-xs text-emerald-900 h-9 flex items-center">
                  −{fmtCurrency(discount)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-emerald-800/80">
              {fmtNumber(minRedeem)} pts needed to redeem.
              Only {fmtNumber(minRedeem - balance)} to go.
            </div>
          )}
        </div>
      )}

      {lookupBusy && !account && (
        <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="loyalty-lookup-busy">
          <Loader2 className="w-3 h-3 animate-spin" /> Checking loyalty…
        </div>
      )}
    </>
  );
};
